import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Rating,
  TextField,
  Button,
  Paper,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';

interface Review {
  _id: string;
  rating: number;
  comment: string;
  userName: string;
  createdAt: string;
}

interface ReviewSectionProps {
  locationId: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ReviewSection: React.FC<ReviewSectionProps> = ({ locationId }) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [newReview, setNewReview] = useState({
    rating: 0,
    comment: '',
    userName: '',
  });
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [locationId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/reviews/location/${locationId}`
      );
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newReview.userName.trim()) {
      setError('Please enter your name');
      return;
    }

    if (newReview.rating === 0) {
      setError('Please select a rating');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/reviews`, {
        locationId,
        ...newReview,
      });
      setNewReview({ rating: 0, comment: '', userName: '' });
      setShowForm(false);
      setShowSuccess(true);
      fetchReviews();
    } catch (error) {
      setError('Failed to submit review');
    }
  };

  const handleDelete = async (reviewId: string) => {
    try {
      await axios.delete(`${API_URL}/api/reviews/${reviewId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
    }
  };

  const handleAddReview = () => {
    setShowForm(true);
    setShowSuccess(false);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Reviews
      </Typography>

      {showForm ? (
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Your Name"
              value={newReview.userName}
              onChange={(e) =>
                setNewReview({ ...newReview, userName: e.target.value })
              }
              margin="normal"
              required
            />
            <Box sx={{ my: 2 }}>
              <Typography component="legend">Rating</Typography>
              <Rating
                value={newReview.rating}
                onChange={(_, value) =>
                  setNewReview({ ...newReview, rating: value || 0 })
                }
              />
            </Box>
            <TextField
              fullWidth
              label="Your Review"
              value={newReview.comment}
              onChange={(e) =>
                setNewReview({ ...newReview, comment: e.target.value })
              }
              multiline
              rows={3}
              margin="normal"
            />
            {error && (
              <Typography color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
            >
              Submit Review
            </Button>
          </form>
        </Paper>
      ) : (
        <Button
          variant="outlined"
          color="primary"
          onClick={handleAddReview}
          sx={{ mb: 3 }}
        >
          Write a Review
        </Button>
      )}

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        message="Review submitted successfully!"
      />

      {reviews.map((review) => (
        <Paper key={review._id} elevation={1} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="subtitle1">{review.userName}</Typography>
              <Rating value={review.rating} readOnly size="small" />
              <Typography variant="body2" color="text.secondary">
                {new Date(review.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
            {localStorage.getItem('token') && (
              <IconButton
                size="small"
                onClick={() => handleDelete(review._id)}
                sx={{ ml: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {review.comment}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};

export default ReviewSection; 